import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type {
  UserOrganizationAffiliation,
  AffiliationInput,
} from "../affiliations.types";

export type UseUpdateUserAffiliationsArgs = {
  onSuccess?: (data: UserOrganizationAffiliation[]) => void;
  onError?: (error: Error) => void;
};

export type UseUpdateUserAffiliationsResult = {
  updateAffiliations: (affiliations: AffiliationInput[]) => void;
  updateAffiliationsAsync: (affiliations: AffiliationInput[]) => Promise<UserOrganizationAffiliation[]>;
  isPending: boolean;
  error: Error | null;
};

/**
 * Update the current user's organization affiliations.
 * Replaces all existing affiliations with the provided list.
 */
export function useUpdateUserAffiliations({
  onSuccess,
  onError,
}: UseUpdateUserAffiliationsArgs = {}): UseUpdateUserAffiliationsResult {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    UserOrganizationAffiliation[],
    Error,
    AffiliationInput[]
  >({
    mutationFn: async (affiliations) => {
      return apiRequest("/profile/affiliations/update/", "POST", {
        affiliations,
      }) as Promise<UserOrganizationAffiliation[]>;
    },
    onSuccess: (data) => {
      // Invalidate and refetch affiliations
      queryClient.invalidateQueries({ queryKey: ["userAffiliations"] });
      // Also invalidate affiliation roles in case new ones were created
      queryClient.invalidateQueries({ queryKey: ["affiliationRoles"] });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });

  return {
    updateAffiliations: mutation.mutate,
    updateAffiliationsAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
